<template>
    <div>
        <h4 v-if="showTitle" style="margin-bottom: 1rem;">{{ task.id ? 'Edit' : 'Add' }}</h4>
        <div class="row">
            <!-- Job Template Selection !-->
            <vue-select
                placeholder="Select job type..."
                :options="templateList"
                v-model="templateProxy"
                container-classes="col"
                :disabled="disabled"
            ></vue-select>

            <!-- Job Sub-Template Single Selection !-->
            <vue-select
                v-if="templateHasOptions && !templateShouldCreatedSubJobs"
                :options="optionList"
                v-model="templateOptionProxy"
                container-classes="col"
                :disabled="disabled"
                @input="optionChanged"
            ></vue-select>

            <!-- Job Sub-Template Multi-Selection !-->
            <div v-else-if="templateHasOptions" class="col-12" style="margin-bottom: 1rem;">
                <multiselect
                    class="multiselect-vue-input-style"
                    :options="optionList"
                    v-model="templateOptionProxy"
                    :disabled="disabled"
                    @input="optionChanged"
                    label="label"
                    track-by="label"
                    multiple
                ></multiselect>
            </div>
        </div>

        <!-- Job Title !-->
        <div v-if="allowTitleInput" class="row" style="margin-bottom: 1rem;">
            <div class="col">
                <input
                    ref="titleTextbox"
                    type="text"
                    v-model="task.title"
                    placeholder="Title" 
                    class="form-control"
                    :disabled="disabled"
                >
            </div>
        </div>

        <!-- Job Description !-->
        <div class="row">
            <div class="col">
                <textarea 
                    v-model="task.description"
                    placeholder="Add more detail if needed..." 
                    class="form-control"
                    :disabled="disabled"
                ></textarea>
            </div>
        </div>

        <!-- Job Retail Pricing !-->
        <div v-if="hasCosts" class="row">
            <div class="col-6">
                <vue-input 
                    v-model="task.invoice_items[0].unit_cost"
                    :label="'Retail Price' + (vatSelectionEnabled ? ' (ex VAT)' : '')"
                    name="selling_price" 
                    type="text" 
                    id="selling_price" 
                    placeholder="e.g. 1000" 
                    addon-before="&pound;"
                    help-text="The price the customer will be charged, leave at zero to include it with the deal."
                    style="margin-top: .5rem;"
                    :disabled="disabled"
                ></vue-input>
            </div>
            <div class="col-6">
                <vue-select
                    v-model="task.invoice_items[0].vat_rate"
                    label="VAT Rate"
                    :options="VatTariffList"
                    style="margin-top: .5rem;"
                    :disabled="!vatSelectionEnabled || disabled"
                ></vue-select>
            </div>
        </div>

        <!-- Job Warranty Toggle !-->
        <div v-if="showWarrantyItemOptions" class="row" style="margin-top: 1rem;">
            <div class="col">
                <ui-switch label="Is this job for a warranty claim?" v-model="task.is_warranty_claim"></ui-switch>
            </div>
        </div>

        <!-- Job Action Buttons !-->
        <div class="row" style="margin-top: 1rem;">
            <div class="col">
                <button :disabled="!canSubmit" class="btn btn-success" @click="saveTask(false)">Save</button>
                <button :disabled="!canSubmit" class="btn btn-success" @click="saveTask(true)">Save &amp; New</button>
                <button v-if="canCancel" class="btn btn-secondary" @click="$emit('cancel')">Cancel</button>
            </div>
            <div class="col-2 text-right">
                <ui-icon-button v-if="canDestroy" @click="$emit('destroy', task)" icon="delete"></ui-icon-button>
            </div>
        </div>
    </div>
</template>

<script>
import {TaskResource, TaskSchema} from '../../../classes/Api/Task';
    import { VatTariffList, VatTariffs } from '../../../classes/VatService'
    import { InvoiceItemSchema } from '../../../classes/Api/Invoice';
    import VueSelect from './Select'
    import Multiselect from 'vue-multiselect'
    import 'vue-multiselect/dist/vue-multiselect.min.css'

    export default {
        components: { VueSelect, Multiselect },
        props: {
            value: {},
            vatSelectionEnabled: {
                default: true,
            },
            showTitle: {
                type: Boolean,
                default: true
            },
            canDestroy: {
                type: Boolean,
                default: true
            },
            canCancel: {
                type: Boolean,
                default: true
            },
            hasCosts: {
                type: Boolean,
                default: true
            },
            additionalValidator: {
                type: Function
            },
            disabled: {
                type: Boolean,
                default: false
            },
            showWarrantyItemOptions: {
                type: Boolean,
                default: false
            },
            context: {
                type: String,
            },
            selectedTemplate: {
                type: Object
            }
        },
        watch: {
            value: {
                handler: function(newVal, oldVal) {
                    if (newVal !== this.task && newVal !== null) {
                        this.task = newVal;
                        this.setTemplate(this.task);
                    } else if(newVal === null) {
                        this.reset();
                    }

                    this.forceTemplateSelection()
                },
                deep: true,
            }
        },
        data() {
            return {
                template: null,
                option: null,
                templates: [],
                task: new TaskSchema({
                    invoice_items: [new InvoiceItemSchema({ vat_rate: VatTariffs.NONE })],
                }),
                VatTariffList
            }
        },
        mounted() {
            if (this.value) {
                this.task = this.value;
            }

            TaskResource.templates(this.context).then(response => {
                this.templates = response.data;

                this.forceTemplateSelection()

                this.$emit('loaded', true);
            }, handleApiError);
        },
        methods: {
            forceTemplateSelection() {
                if (this.selectedTemplate) {
                    this.templates = this.templates.filter(template => {
                        return template.id === this.selectedTemplate.id
                    })

                    this.template = this.templates[0]
                }
            },
            optionChanged(option) {
                this.$nextTick(() => {
                    if (this.$refs.titleTextbox && !option.prevent_title_modification) {
                        this.task.title = ''
                        this.$refs.titleTextbox.focus()
                    }
                })
            },
            setTemplate(task) {
                // Set the main template
                let templateId = task.template_id;

                this.template = this.templates.find(item => {
                    return parseInt(item.id) === parseInt(templateId);
                });

                // Set the option or options.
                this.option = null

                if (task.children && task.children.length > 0) {
                  this.option = task.children.map(childTask => {
                      const option = this.template.options.find(
                          option => childTask.template_id === option.id
                      )

                      return { label: option.title, value: option }
                  })
                } else if (task.template_type === 'task.template.option') {
                    let option;

                    let template = this.templates.find(item => {
                        option = item.options.find(subItem => {
                            return parseInt(subItem.id) === parseInt(templateId);
                        })

                        return option ? true : false;
                    });

                    this.template = template;
                    this.option = option;
                }
            },
            setTask() {
                this.task.title = this.template.title;
                this.task.template_type = 'task.template';
                this.task.template_id = this.template.id;
                this.setTaskInvoiceItem()

                if (this.option && this.option.constructor.name === 'Array') {
                    const originalTaskChildren = this.value.children
                    this.task.children = []

                    // Create child tasks for each option selected.
                    this.option.forEach(option => {
                        this.task.children.push({
                            title: this.task.title + ' - ' + option.value.title,
                            template_type: 'task.template.option',
                            template_id: option.value.id,
                        })
                    })

                    // Add records for removed children.
                    for (const child of originalTaskChildren) {
                        if (!this.task.children.find(c => c.id === child.id)) {
                            this.task.children.push({ ...child, removed: true })
                        }
                    }

                    this.setTaskInvoiceItem()
                } else if (this.option) {
                    this.task.title += ' - ' + this.option.title;
                    this.task.template_type = 'task.template.option';
                    this.task.template_id = this.option.id;
                    this.setTaskInvoiceItem()
                }
            },
            setTaskInvoiceItem() {
                if (this.hasCosts) {
                    this.task.invoice_items[0].description = this.task.title
                }
            },
            saveTask(showNew = false) {
                this.setTaskInvoiceItem()
                this.$emit('save', this.task, showNew);
                this.reset();
            },
            reset() {
                this.template = null;
                this.option = null;
                this.task = new TaskSchema({
                    invoice_items: [new InvoiceItemSchema({ vat_rate: VatTariffs.NONE })],
                });

                this.forceTemplateSelection()
            }
        },
        computed: {
            templateShouldCreatedSubJobs() {
                return this.templateProxy.supports_sub_jobs
            },
            templateHasOptions() {
                return this.templateProxy && this.templateProxy.options.length > 0
            },
            allowTitleInput() {
                return this.templateProxy && !this.templateProxy.prevent_title_modification ||
                    this.templateOptionProxy && this.templateOptionProxy.constructor.name !== 'Array' &&
                    !this.templateOptionProxy.prevent_title_modification && this.templateProxy.options.length > 0
            },
            additionalValidationPasses() {
                if (typeof this.additionalValidator === 'function') {
                    return this.additionalValidator(this.task)
                }

                return true
            },
            hasValidTitle() {
                return this.task.title && this.task.title.length > 0
            },
            canSubmit() {
                if (this.disabled) {
                    return false;
                }

                if (this.template && this.template.options.length > 0) {
                    return this.hasValidTitle && this.option && this.additionalValidationPasses ? true : false;
                }

                return this.hasValidTitle && this.template && this.additionalValidationPasses ? true : false;
            },
            templateList() {
                let list = [];

                for (let i in this.templates) {
                    if (this.templates[i].show_in_lists) {
                        list.push({value: this.templates[i], label: this.templates[i].title})
                    }
                }

                return list
            },
            optionList() {
                return this.template.options.map(item => { return { label: item.title, value: item }});
            },
            templateProxy: {
                get() {
                    return this.template;
                },
                set(value) {
                    this.template = value;
                    this.$nextTick(() => {
                        this.option = null
                        this.setTask();
                    })
                }
            },
            templateOptionProxy: {
                get() {
                    return this.option;
                },
                set(value) {
                    this.option = value;
                    this.$nextTick(() => {
                        this.setTask();
                    })
                }
            }
        }
    }
</script>